import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import { useLocale } from '@/hooks';
import { PlanBadge } from '@/shared/components/Badge/PlanBadge';
import { type MapTemplate, MapTemplateTypeLocaleKey } from '@/types';
import { cn } from '@/utils';
interface Props {
  map: MapTemplate;
  showMapType?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}
export const MapTemplateCard = ({
  map,
  showMapType = true,
  isSelected,
  onClick
}: Props) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    locale
  } = useLocale();
  const [openedTooltip, setOpenedTooltip] = useState<'NEW' | 'RECOMMEND' | null>(null);
  const name = (map.serializedLocalize[locale] ?? map.serializedLocalize.en).name;
  const handleClickTooltip = (type: 'NEW' | 'RECOMMEND') => {
    setOpenedTooltip(prev => prev === type ? null : type);
  };
  return <div id={`map-${map.templateId}`} className={cn('flex w-full cursor-pointer gap-sm items-center border rounded-md border-alternative bg-white p-sm hover:bg-strong-alternative overflow-hidden', isSelected && 'shadow-sm outline outline-2 outline-strong -outline-offset-2')} onClick={onClick} data-sentry-component="MapTemplateCard" data-sentry-source-file="MapTemplateCard.tsx">
      <div className="relative h-[48px] w-[73px] shrink-0">
        <img className="size-full rounded-sm" src={map.thumbnailImage} alt={name} />
        {map.templatePlanType !== 'FREE' && <PlanBadge className="absolute -left-[4px] -top-[4px]" planType={map.templatePlanType} tooltip={false} />}
      </div>

      <div className="flex flex-col gap-4xs">
        <div className="flex items-center gap-4xs">
          <h5 className={cn('text-body-lg-bold mobile-lg:text-body-md-bold', showMapType ? 'line-clamp-1' : 'line-clamp-2')}>
            {name}
          </h5>
          {map.hasNewTag && <Tooltip side="top" className="bg-white text-default" arrow={{
          fill: '#FFF'
        }} trigger={<NewBadge className="shrink-0" onClick={() => handleClickTooltip('NEW')} />} content={t('build:newBadge')} open={openedTooltip === 'NEW'} />}
          {map.isRecommended && <Tooltip side="top" className="bg-white text-default" arrow={{
          fill: '#FFF'
        }} trigger={<RecommendBadge className="shrink-0" onClick={() => handleClickTooltip('RECOMMEND')} />} content={t('build:teacherRecommended')} open={openedTooltip === 'RECOMMEND'} />}
        </div>
        {showMapType && <span className="text-caption-md-medium text-assistive">
            {t(MapTemplateTypeLocaleKey[map.type])}
          </span>}
      </div>
    </div>;
};

// Windows 디자인 이슈로 인라인 아이콘 사용
const NewBadge = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => {
  return <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" {...props}>
        <g clipPath="url(#clip0_30074_81342)">
          <mask id="mask0_30074_81342" style={{
        maskType: 'luminance'
      }} maskUnits="userSpaceOnUse" x="0" y="0" width="12" height="12">
            <path d="M0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6Z" fill="white" />
          </mask>
          <g mask="url(#mask0_30074_81342)">
            <path d="M0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6Z" fill="#FF737B" />
            <path d="M8.43359 3.34375V9H7.19141L5.08984 5.94531H5.05078V9H3.56641V3.34375H4.83203L6.90234 6.38281H6.94922V3.34375H8.43359Z" fill="white" />
          </g>
          <mask id="mask1_30074_81342" style={{
        maskType: 'luminance'
      }} maskUnits="userSpaceOnUse" x="0" y="0" width="12" height="12">
            <path d="M0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6Z" fill="white" />
          </mask>
          <g mask="url(#mask1_30074_81342)">
            <path d="M6 12V11C3.23858 11 1 8.76142 1 6H0H-1C-1 9.86599 2.13401 13 6 13V12ZM12 6H11C11 8.76142 8.76142 11 6 11V12V13C9.86599 13 13 9.86599 13 6H12ZM6 0V1C8.76142 1 11 3.23858 11 6H12H13C13 2.13401 9.86599 -1 6 -1V0ZM6 0V-1C2.13401 -1 -1 2.13401 -1 6H0H1C1 3.23858 3.23858 1 6 1V0Z" fill="#1C1B1F" fillOpacity="0.08" />
          </g>
        </g>
        <defs>
          <clipPath id="clip0_30074_81342">
            <rect width="12" height="12" fill="white" />
          </clipPath>
        </defs>
      </svg>;
});
NewBadge.displayName = 'NewBadge';
const RecommendBadge = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => <svg ref={ref} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" {...props}>
    <path d="M6.21024 3.92577C6.88061 3.08779 8.13055 3.01857 8.88937 3.77738C9.56897 4.45699 9.59499 5.55055 8.94848 6.26171L6.34472 9.12585C6.27778 9.19948 6.18288 9.24146 6.08336 9.24146C5.98385 9.24146 5.88895 9.19948 5.82201 9.12585L3.21825 6.26171C2.57174 5.55055 2.59776 4.45699 3.27736 3.77738C4.03618 3.01857 5.28612 3.08779 5.9565 3.92577L6.08336 4.08435L6.21024 3.92577Z" fill="#FF737B" />
    <path d="M11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11V12C2.68629 12 0 9.31371 0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6C12 9.31371 9.31371 12 6 12V11C8.76142 11 11 8.76142 11 6Z" fill="#FFACB6" />
  </svg>);
RecommendBadge.displayName = 'RecommendBadge';